import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";


const API_URL = process.env.REACT_APP_API_URL;


// Fetch all patients
export const fetchAllPatients = createAsyncThunk(
  "patients/fetchAllPatients",
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth.token || sessionStorage.getItem("access_token");
      const response = await axios.get(`${API_URL}/patient/patients/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch patients"
      );
    }
  }
);


// Update a patient
export const updatePatient = createAsyncThunk(
  "patients/updatePatient",
  async ({ patientData }, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth.token || sessionStorage.getItem("access_token");
      const { patient_id, ...updatedData } = patientData;
      const response = await axios.put(
        `${API_URL}/patient/patients/${patient_id}/`,
        updatedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to update patient"
      );
    }
  }
);


// Delete a patient
export const deletePatient = createAsyncThunk(
  "patients/deletePatient",
  async (patient_id, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth.token || sessionStorage.getItem("access_token");
      await axios.delete(`${API_URL}/patient/patients/${patient_id}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return patient_id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to delete patient"
      );
    }
  }
);


// Fetch patients assigned to a user
export const fetchPatientsByUser = createAsyncThunk(
  "patients/fetchByUser",
  async (userId, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth.token || sessionStorage.getItem("access_token");
      const response = await axios.get(
        `${API_URL}/patient/user-patients/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch patients for user"
      );
    }
  }
);


// New thunk: Fetch vitals history for a patient in selected duration
export const fetchPatientVitalsHistory = createAsyncThunk(
  "patients/fetchVitalsHistory",
  async ({ patient_id, duration }, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth.token || sessionStorage.getItem("access_token");
      const response = await axios.get(
        `${API_URL}/patient/${patient_id}/vitals-history`,
        {
          params: { duration },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch patient vitals history"
      );
    }
  }
);


// Fetch single patient details by patientId
export const fetchPatientDetails = createAsyncThunk(
  "patients/fetchPatientDetails",
  async (patientId, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth.token || sessionStorage.getItem("access_token");
      const response = await axios.get(`${API_URL}/patient/patients/${patientId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch patient details");
    }
  }
);



const patientSlice = createSlice({
  name: "patients",
  initialState: {
    patients: [],
    loading: false,
    error: null,
    vitalsHistory: [],           // New: store vitals history data
    vitalsHistoryLoading: false,
    vitalsHistoryError: null,
    patientDetails: null,
    patientDetailsLoading: false,
    patientDetailsError: null,
  },
  reducers: {
    clearPatients: (state) => {
      state.patients = [];
      state.error = null;
      state.loading = false;
    },
    clearVitalsHistory: (state) => {
      state.vitalsHistory = [];
      state.vitalsHistoryLoading = false;
      state.vitalsHistoryError = null;
    },
    clearPatientDetails: (state) => {
      state.patientDetails = null;
      state.patientDetailsLoading = false;
      state.patientDetailsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all patients
      .addCase(fetchAllPatients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllPatients.fulfilled, (state, action) => {
        state.loading = false;
        state.patients = action.payload;
      })
      .addCase(fetchAllPatients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })


      // Update patient
      .addCase(updatePatient.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.patients.findIndex(
          (p) => p.patient_id === updated.patient_id
        );
        if (index !== -1) {
          state.patients[index] = updated;
        }
      })
      .addCase(updatePatient.rejected, (state, action) => {
        state.error = action.payload;
      })


      // Fetch patients assigned to user
      .addCase(fetchPatientsByUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientsByUser.fulfilled, (state, action) => {
        state.loading = false;
        state.patients = action.payload;
      })
      .addCase(fetchPatientsByUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })


      // Delete patient
      .addCase(deletePatient.fulfilled, (state, action) => {
        const id = action.payload;
        state.patients = state.patients.filter((p) => p.patient_id !== id);
      })
      .addCase(deletePatient.rejected, (state, action) => {
        state.error = action.payload;
      })


      // New: Fetch patient vitals history
      .addCase(fetchPatientVitalsHistory.pending, (state) => {
        state.vitalsHistoryLoading = true;
        state.vitalsHistoryError = null;
      })
      .addCase(fetchPatientVitalsHistory.fulfilled, (state, action) => {
        state.vitalsHistoryLoading = false;
        state.vitalsHistory = action.payload;
      })
      .addCase(fetchPatientVitalsHistory.rejected, (state, action) => {
        state.vitalsHistoryLoading = false;
        state.vitalsHistoryError = action.payload;
      })
      // Fetch single patient details
      .addCase(fetchPatientDetails.pending, (state) => {
        state.patientDetailsLoading = true;
        state.patientDetailsError = null;
      })
      .addCase(fetchPatientDetails.fulfilled, (state, action) => {
        state.patientDetailsLoading = false;
        state.patientDetails = action.payload;
      })
      .addCase(fetchPatientDetails.rejected, (state, action) => {
        state.patientDetailsLoading = false;
        state.patientDetailsError = action.payload;
      });
  },
});


export const { clearPatients, clearVitalsHistory, clearPatientDetails } = patientSlice.actions;
export const selectPatientDetails = (state) => ({
  info: state.patients.patientDetails,
  loading: state.patients.patientDetailsLoading,
  error: state.patients.patientDetailsError,
});
export default patientSlice.reducer;
